
import { useState, useEffect } from 'react';
import { useToast } from './use-toast';
import { PortfolioItem, NewPortfolioItemFormData } from '@/services/portfolio/types';
import { 
  fetchPortfolioItems, 
  fetchPortfolioItemsByTaxonomy,
  createPortfolioItem,
  updatePortfolioItem,
  deletePortfolioItem,
  uploadPortfolioMedia
} from '@/services/portfolio/portfolioService';
import { useAuth } from '@/contexts/auth/AuthContext';

export const usePortfolio = (taxonomyLevel?: string) => {
  const [items, setItems] = useState<PortfolioItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const loadItems = async () => {
    setIsLoading(true);
    try {
      let data;
      if (taxonomyLevel) {
        data = await fetchPortfolioItemsByTaxonomy(taxonomyLevel);
      } else {
        data = await fetchPortfolioItems();
      }
      setItems(data);
    } catch (error) {
      console.error('Error loading portfolio items:', error);
      toast({
        title: 'Error loading portfolio',
        description: 'There was a problem loading your portfolio items.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addPortfolioItem = async (formData: NewPortfolioItemFormData) => {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'You must be logged in to add portfolio items',
        variant: 'destructive',
      });
      return null;
    }

    setIsSubmitting(true);
    try {
      let mediaUrl = formData.media_url;
      
      // Process based on content type
      const itemData = {
        title: formData.title,
        description: formData.description,
        item_type: formData.item_type as PortfolioItem['item_type'],
        content: formData.content_type === 'text' ? formData.content : null,
        media_url: (formData.content_type === 'image' || formData.content_type === 'video') 
          ? formData.media_url 
          : null,
        taxonomy_level: formData.taxonomy_level as PortfolioItem['taxonomy_level'],
      };

      const newItem = await createPortfolioItem(itemData);
      
      if (newItem) {
        setItems((prev) => [newItem, ...prev]);
        toast({
          title: 'Portfolio item added',
          description: 'Your portfolio item has been added successfully.',
        });
        return newItem;
      }
      return null;
    } catch (error) {
      console.error('Error adding portfolio item:', error);
      toast({
        title: 'Error adding item',
        description: 'There was a problem adding your portfolio item.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const removePortfolioItem = async (id: string) => {
    try {
      const success = await deletePortfolioItem(id);
      if (success) {
        setItems((prev) => prev.filter((item) => item.id !== id));
        toast({
          title: 'Item removed',
          description: 'Portfolio item has been removed successfully.',
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing portfolio item:', error);
      toast({
        title: 'Error removing item',
        description: 'There was a problem removing the portfolio item.',
        variant: 'destructive',
      });
      return false;
    }
  };

  const uploadMedia = async (file: File) => {
    try {
      const url = await uploadPortfolioMedia(file);
      if (url) {
        toast({
          title: 'File uploaded',
          description: 'Your file has been uploaded successfully.',
        });
        return url;
      }
      toast({
        title: 'Upload failed',
        description: 'There was a problem uploading your file.',
        variant: 'destructive',
      });
      return null;
    } catch (error) {
      console.error('Error uploading media:', error);
      toast({
        title: 'Upload error',
        description: 'There was a problem uploading your file.',
        variant: 'destructive',
      });
      return null;
    }
  };

  useEffect(() => {
    if (user) {
      loadItems();
    } else {
      setItems([]);
      setIsLoading(false);
    }
  }, [user, taxonomyLevel]);

  return {
    items,
    isLoading,
    isSubmitting,
    loadItems,
    addPortfolioItem,
    removePortfolioItem,
    uploadMedia,
  };
};
